import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PeriodSelector from "components/DynamicReportInputs/PeriodSelector";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTablev2";
import PageTitle from "components/PageTitle/PageTitle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getIndependentPharmacyActivityThreadStatus,
  mapCompletedIndependentPharmacyActivityThread,
  startIndependentPharmacyActivityThread,
} from "services/independentPharmacyActivityService";

/**
 * @typedef {{ period: number, enrolled_pharmacies: number, active_pharmacies: number, new_pharmacies_with_sales: number }} ActivityApiRow
 * @typedef {{ period: number, month: string, enrolledPharmacies: number, activePharmacies: number, newPharmaciesWithSales: number }} ActivityRow
 */

const SUMMARY_FILENAME_PREFIX = "reporte_actividad_independientes_";
const DETAIL_FILENAME_PREFIX = "detalle_farmacias_con_actividad_";
const DOCUMENTATION_LINK =
  "https://cfr-config.s3.sa-east-1.amazonaws.com/cfr_rules_documentation/definiciones_actividad_farmacias_independientes.pdf";
const PERIOD_PATTERN = /^\d{6}$/;
const MIN_PERIOD = 202501;
const MIN_PERIOD_DATE = new Date(2025, 0, 1);
const GENERIC_REQUEST_ERROR =
  "No se pudo completar la operación. Intente nuevamente.";
const NETWORK_ERROR_PATTERN =
  /failed to fetch|network ?error|network request failed|load failed/i;
const POLLING_INTERVAL_MS = 2000;
const INITIAL_POLLING_DELAY_MS = 3000;

const columns = [
  { name: "Mes", options: { sort: false } },
  { name: "Farmacias independientes enroladas", options: { sort: false } },
  {
    name: "Farmacias independientes con ventas en el mes",
    options: { sort: false },
  },
  {
    name: "Farmacias nuevas con ventas en el mes del alta",
    options: { sort: false },
  },
];

const useStyles = makeStyles((theme) => ({
  paper: { padding: theme.spacing(3), background: "#f5f5f5" },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    justifyContent: "center",
  },
  table: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
}));

function isValidPeriod(value) {
  const period = String(value ?? "");
  const month = Number(period.slice(4, 6));
  return PERIOD_PATTERN.test(period) && month >= 1 && month <= 12;
}

export function validatePeriodRange(fromPeriod, toPeriod) {
  const errors = {};

  if (!fromPeriod) errors.fromPeriod = "Informe el período inicial.";
  else if (!isValidPeriod(fromPeriod))
    errors.fromPeriod = "El período inicial debe tener formato AAAAMM.";
  else if (Number(fromPeriod) < MIN_PERIOD)
    errors.fromPeriod = "El período inicial no puede ser anterior a 01/2025.";

  if (!toPeriod) errors.toPeriod = "Informe el período final.";
  else if (!isValidPeriod(toPeriod))
    errors.toPeriod = "El período final debe tener formato AAAAMM.";
  else if (Number(toPeriod) < MIN_PERIOD)
    errors.toPeriod = "El período final no puede ser anterior a 01/2025.";

  const normalizedFrom = errors.fromPeriod ? null : Number(fromPeriod);
  const normalizedTo = errors.toPeriod ? null : Number(toPeriod);

  if (
    normalizedFrom !== null &&
    normalizedTo !== null &&
    normalizedFrom > normalizedTo
  ) {
    errors.fromPeriod =
      "El período inicial no puede ser posterior al período final.";
    errors.toPeriod =
      "El período final no puede ser anterior al período inicial.";
  }

  return {
    fromPeriod: normalizedFrom,
    toPeriod: normalizedTo,
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

/** @param {{ data: ActivityApiRow[] }} response @returns {ActivityRow[]} */
export function mapActivityResponse(response) {
  if (!Array.isArray(response?.data)) {
    throw new Error(
      "La respuesta del panel no contiene una lista de datos válida.",
    );
  }

  return response.data
    .map((item) => {
      if (
        ![
          item?.period,
          item?.enrolled_pharmacies,
          item?.active_pharmacies,
          item?.new_pharmacies_with_sales,
        ].every(Number.isFinite)
      ) {
        throw new Error("La respuesta del panel contiene una fila inválida.");
      }

      const period = String(item.period);
      if (!isValidPeriod(period)) {
        throw new Error(`Período inválido recibido: ${period}`);
      }

      return {
        period: item.period,
        month: `${period.slice(4, 6)}/${period.slice(0, 4)}`,
        enrolledPharmacies: item.enrolled_pharmacies,
        activePharmacies: item.active_pharmacies,
        newPharmaciesWithSales: item.new_pharmacies_with_sales,
      };
    })
    .sort((left, right) => left.period - right.period);
}

export function selectOutputUrl(outputFiles, filenamePrefix, fileLabel) {
  const entry = Object.entries(outputFiles || {}).find(([filename]) =>
    filename.startsWith(filenamePrefix),
  );
  const url = entry?.[1];

  if (!url) {
    throw new Error(
      `El reporte finalizó correctamente, pero no devolvió el archivo de ${fileLabel} esperado.`,
    );
  }
  return url;
}

function getErrorMessage(error) {
  const localized = error?.errors?.description_es;
  const directLocalized = error?.description_es;
  const threadError = error?.error_message?.description_es;

  if (
    error instanceof TypeError ||
    NETWORK_ERROR_PATTERN.test(String(error?.message || error || ""))
  ) {
    return GENERIC_REQUEST_ERROR;
  }

  return (
    (Array.isArray(localized) ? localized[0] : localized) ||
    (Array.isArray(directLocalized) ? directLocalized[0] : directLocalized) ||
    (Array.isArray(threadError) ? threadError[0] : threadError) ||
    error?.error ||
    error?.message ||
    GENERIC_REQUEST_ERROR
  );
}

function getOutputUrl(outputFiles, kind) {
  return kind === "summary"
    ? selectOutputUrl(outputFiles, SUMMARY_FILENAME_PREFIX, "resumen")
    : selectOutputUrl(outputFiles, DETAIL_FILENAME_PREFIX, "detalle");
}

function openDownloadUrl(url) {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function ActivityTable({ status, rows }) {
  if (status === "idle") {
    return null;
  }

  return (
    <CustomMUIDataTable
      title="Resultados"
      data={rows.map((row) => [
        row.month,
        row.enrolledPharmacies,
        row.activePharmacies,
        row.newPharmaciesWithSales,
      ])}
      columns={columns}
      count={rows.length}
      page={0}
      totalPages={1}
      rowsPerPage={Math.max(rows.length, 5)}
      changePage={() => {}}
      changeRowsPerPage={() => {}}
      options={{
        pagination: false,
        filter: false,
        search: false,
        download: false,
        isLoading: status === "loading",
      }}
    />
  );
}

export default function IndependentPharmacyActivityPage() {
  const classes = useStyles();
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const progressTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const [fromPeriod, setFromPeriod] = useState("");
  const [toPeriod, setToPeriod] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [rows, setRows] = useState([]);
  const [tableStatus, setTableStatus] = useState("idle");
  const [operation, setOperation] = useState(null);
  const [completedReport, setCompletedReport] = useState(null);
  const isBusy = operation !== null;

  const clearProgressTimeout = useCallback(() => {
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearProgressTimeout();
    };
  }, [clearProgressTimeout]);

  const changePeriod = (setter, errorKey) => (value) => {
    setter(value);
    setFieldErrors((current) => ({ ...current, [errorKey]: undefined }));
    setCompletedReport(null);
  };

  const getValidatedRange = useCallback(() => {
    const validation = validatePeriodRange(fromPeriod, toPeriod);
    setFieldErrors(validation.errors);
    return validation.isValid
      ? {
          fromPeriod: validation.fromPeriod,
          toPeriod: validation.toPeriod,
          key: `${validation.fromPeriod}-${validation.toPeriod}`,
        }
      : null;
  }, [fromPeriod, toPeriod]);

  const finishWithError = useCallback(
    (error) => {
      if (!mountedRef.current) return;
      setRows([]);
      setCompletedReport(null);
      setTableStatus("idle");
      setOperation(null);
      snackbarOpen("error", getErrorMessage(error));
    },
    [snackbarOpen],
  );

  const updateProgress = useCallback(
    function pollActivityReport(requestId, range) {
      progressTimeoutRef.current = null;
      getIndependentPharmacyActivityThreadStatus(requestId)
        .then((response) => {
          if (!mountedRef.current) return;

          if (response.status === "Completed") {
            const report =
              mapCompletedIndependentPharmacyActivityThread(response);
            setRows(mapActivityResponse(report));
            setCompletedReport({
              key: range.key,
              outputFiles: report.output_files,
            });
            setTableStatus("success");
            setOperation(null);
            return;
          }

          if (!response.error_status && response.status !== "Failure") {
            progressTimeoutRef.current = setTimeout(
              () => pollActivityReport(requestId, range),
              POLLING_INTERVAL_MS,
            );
            return;
          }

          finishWithError(
            response?.error_message ||
              new Error("El reporte falló durante la generación."),
          );
        })
        .catch(finishWithError);
    },
    [finishWithError],
  );

  const handleApply = async () => {
    const range = getValidatedRange();
    if (!range) return;
    clearProgressTimeout();
    setOperation("query");
    setTableStatus("loading");

    try {
      const { id } = await startIndependentPharmacyActivityThread(
        range.fromPeriod,
        range.toPeriod,
      );
      if (!mountedRef.current) return;
      progressTimeoutRef.current = setTimeout(
        () => updateProgress(id, range),
        INITIAL_POLLING_DELAY_MS,
      );
    } catch (error) {
      finishWithError(error);
    }
  };

  const handleDownload = (kind) => {
    const range = getValidatedRange();
    if (!range) return;

    if (completedReport?.key !== range.key) {
      snackbarOpen(
        "error",
        "Aplicá los períodos antes de descargar el reporte.",
      );
      return;
    }

    try {
      openDownloadUrl(getOutputUrl(completedReport.outputFiles, kind));
    } catch (error) {
      snackbarOpen("error", getErrorMessage(error));
    }
  };

  return (
    <Grid>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <PageTitle title="Actividad de Farmacias Independientes" />
      <Paper elevation={3} className={classes.paper}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Typography variant="h4">Selección</Typography>
              <Button
                component="a"
                href={DOCUMENTATION_LINK}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="text"
              >
                Ver Documentación
              </Button>
            </Grid>
            <Divider />
          </Grid>

          <Grid item xs={12} md={6}>
            <PeriodSelector
              label="Período inicial"
              value={fromPeriod}
              onChange={changePeriod(setFromPeriod, "fromPeriod")}
              error={fieldErrors.fromPeriod}
              minDate={MIN_PERIOD_DATE}
              required
              disabled={isBusy}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PeriodSelector
              label="Período final"
              value={toPeriod}
              onChange={changePeriod(setToPeriod, "toPeriod")}
              error={fieldErrors.toPeriod}
              minDate={MIN_PERIOD_DATE}
              required
              disabled={isBusy}
            />
          </Grid>

          <Grid item xs={12} className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={isBusy}
              startIcon={
                operation === "query" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
            >
              {operation === "query" ? "Aplicando…" : "Aplicar"}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleDownload("summary")}
              disabled={isBusy || !completedReport}
              startIcon={<GetAppIcon />}
            >
              Descargar
            </Button>
          </Grid>

          <Grid item xs={12} className={classes.table}>
            <ActivityTable status={tableStatus} rows={rows} />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
